import { type CSSProperties, type FormEvent, type ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { Check, ChevronDown, ListFilter, Search, Upload, X } from 'lucide-react'
import {
  getChannelCoverUrl,
  resolveMediaUrl,
  type AudioGroup,
  type Channel,
  type ChannelIncludeMatchMode,
  type ChannelLabelRuleMode,
  type ChannelSourceQuality,
  type ChannelTrackSelectionMode,
  type StreamingFormat
} from '../../api'
import type { Label } from '../../api/labels'
import type { VisualGroup } from '../../api/visuals'
import { channelColorThemes, getChannelColorTheme, type ChannelColorThemeId } from '../../channelColorThemes'
import { labelCategoryLabels, labelCategoryOptions } from '../labels/labelDisplay'
import { labelChipClassName, labelMoreClassName, labelUiStyles } from '../labels/labelUiStyles'
import { Button, LabelBrowser, MobileEditorSheet, ToggleSwitch, labelBrowserClassName } from '../../components/ui'
import { normalizeLabelText } from '../labels/labelText'
import { GroupSelect } from './GroupSelect'
import { StreamControlPanel } from './StreamControlPanel'
import styles from './ChannelDialog.module.css'

type ChannelDialogProps = {
  channel: Channel
  visualGroups: VisualGroup[]
  audioGroups: AudioGroup[]
  labels: Label[]
  onClose: () => void
  onSave: (input: {
    id: string
    name: string
    slug: string
    description: string | null
    color_theme: ChannelColorThemeId
    is_active: boolean
    sort_order: number
    track_selection_mode: ChannelTrackSelectionMode
    include_match_mode: ChannelIncludeMatchMode
    bpm_min: number | null
    bpm_max: number | null
    stream_bitrate: number
    stream_format: StreamingFormat
    source_quality: ChannelSourceQuality
    visualGroupIds: string[]
    audioGroupIds: string[]
    labelRules: Array<{ labelId: string; mode: ChannelLabelRuleMode; weight?: number }>
    coverFile: File | null
  }) => Promise<void>
}

type ChannelDialogTab = 'details' | 'appearance' | 'visual' | 'audio' | 'streaming'
type LabelRuleSection = 'include' | 'boost' | 'exclude'

const channelDialogTabs: Array<{ id: ChannelDialogTab; label: string }> = [
  { id: 'details', label: 'Details' },
  { id: 'appearance', label: 'Appearance' },
  { id: 'visual', label: 'Visual' },
  { id: 'audio', label: 'Audio' },
  { id: 'streaming', label: 'Streaming' }
]

const cx = (...classNames: Array<string | false | null | undefined>): string =>
  classNames
    .filter(Boolean)
    .map((className) => styles[className as string] ?? className)
    .join(' ')

const lb = labelBrowserClassName

const generateSlug = (name: string): string =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')

const getSortedSelectedLabels = (labels: Label[], selectedIds: string[]): Label[] =>
  labels
    .filter((label) => selectedIds.includes(label.id))
    .sort((left, right) => {
      const categoryDiff = labelCategoryOptions.indexOf(left.category) - labelCategoryOptions.indexOf(right.category)

      return categoryDiff || left.name.localeCompare(right.name)
    })

const ChannelRuleLabelPanel = ({
  description,
  labels,
  onBrowse,
  onChange,
  onToggle,
  open,
  selectedIds,
  title
}: {
  description: string
  labels: Label[]
  onBrowse: () => void
  onChange: (ids: string[]) => void
  onToggle: () => void
  open: boolean
  selectedIds: string[]
  title: string
}) => {
  const selectedLabels = getSortedSelectedLabels(labels, selectedIds)
  const groupedSelectedLabels = labelCategoryOptions
    .map((category) => ({
      category,
      items: selectedLabels.filter((label) => label.category === category)
    }))
    .filter((group) => group.items.length > 0)

  return (
    <section className={cx('channel-rule-label-panel', open ? 'open' : 'collapsed')}>
      <div className={labelUiStyles.header}>
        <button
          aria-expanded={open}
          className={cx('channel-rule-label-toggle')}
          onClick={onToggle}
          type="button"
        >
          <ChevronDown size={16} />
          <span className={labelUiStyles.title}>
            <span className="form-label">{title}</span>
            {selectedLabels.length > 0 && (
              <span className={labelUiStyles.count} aria-label={`${selectedLabels.length} selected`}>
                {selectedLabels.length}
              </span>
            )}
          </span>
        </button>
        <div className={labelUiStyles.primaryActions}>
          <Button
            className={labelUiStyles.browse}
            onClick={onBrowse}
            variant="primary"
            aria-label={`Browse ${title.toLowerCase()}`}
          >
            <ListFilter size={16} /> <span className={labelUiStyles.actionText}>Browse</span>
          </Button>
        </div>
      </div>
      {open && (
        <>
          <p className="form-label-hint">{description}</p>
          {selectedLabels.length > 0 ? (
            <div className={labelUiStyles.appliedBody} aria-label={`${title} selected labels`}>
              {groupedSelectedLabels.map((group) => (
                <div className={labelUiStyles.appliedGroup} key={group.category}>
                  <span className={labelUiStyles.categoryTitle}>{labelCategoryLabels[group.category]}</span>
                  <div className={labelUiStyles.selectedLabels}>
                    {group.items.map((label) => (
                      <button
                        className={labelUiStyles.selectedLabel}
                        key={label.id}
                        onClick={() => onChange(selectedIds.filter((id) => id !== label.id))}
                        title={`Remove ${label.name}`}
                        type="button"
                      >
                        <span>{label.name}</span>
                        <X size={13} />
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className={labelUiStyles.collapsed}>
              No labels selected. Use Browse to pick from the label dictionary.
            </div>
          )}
        </>
      )}
      {!open && selectedLabels.length > 0 && (
        <div className={cx('channel-rule-label-preview')} aria-label={`${title} preview`}>
          {selectedLabels.slice(0, 4).map((label) => (
            <span className={labelChipClassName(label.category)} key={label.id}>
              {label.name}
            </span>
          ))}
          {selectedLabels.length > 4 && (
            <span className={labelMoreClassName}>+{selectedLabels.length - 4}</span>
          )}
        </div>
      )}
    </section>
  )
}

export const ChannelDialog = (props: ChannelDialogProps) => {
  const { channel, visualGroups, audioGroups, labels } = props

  const [name, setName] = useState(channel.name)
  const [slug, setSlug] = useState(channel.slug)
  const [description, setDescription] = useState(channel.description ?? '')
  const [colorTheme, setColorTheme] = useState(getChannelColorTheme(channel.color_theme).id)
  const [isActive, setIsActive] = useState(channel.is_active)
  const [sortOrder, setSortOrder] = useState(channel.sort_order)
  const [trackSelectionMode, setTrackSelectionMode] = useState<ChannelTrackSelectionMode>(channel.track_selection_mode ?? 'groups')
  const [includeMatchMode, setIncludeMatchMode] = useState<ChannelIncludeMatchMode>(channel.include_match_mode ?? 'all')
  const [bpmMinInput, setBpmMinInput] = useState(channel.bpm_min?.toString() ?? '')
  const [bpmMaxInput, setBpmMaxInput] = useState(channel.bpm_max?.toString() ?? '')
  const [streamBitrate, setStreamBitrate] = useState(channel.stream_bitrate ?? 256)
  const [sourceQuality, setSourceQuality] = useState<ChannelSourceQuality>(channel.source_quality ?? 'mp3_only')
  const streamFormat: StreamingFormat = channel.stream_format ?? 'mp3'
  const [visualGroupIds, setVisualGroupIds] = useState(channel.visual_groups.map((g) => g.id))
  const [audioGroupIds, setAudioGroupIds] = useState(channel.audio_groups.map((g) => g.id))
  const [includeLabelIds, setIncludeLabelIds] = useState(channel.label_rules.filter((rule) => rule.mode === 'include').map((rule) => rule.label_id))
  const [excludeLabelIds, setExcludeLabelIds] = useState(channel.label_rules.filter((rule) => rule.mode === 'exclude').map((rule) => rule.label_id))
  const [boostLabelIds, setBoostLabelIds] = useState(channel.label_rules.filter((rule) => rule.mode === 'boost').map((rule) => rule.label_id))
  const [labelBrowserSection, setLabelBrowserSection] = useState<LabelRuleSection | null>(null)
  const [browseSelectedLabelIds, setBrowseSelectedLabelIds] = useState<string[]>([])
  const [browseCategory, setBrowseCategory] = useState<Label['category'] | 'all'>('all')
  const [labelSearch, setLabelSearch] = useState('')
  const [openLabelRuleSections, setOpenLabelRuleSections] = useState<Record<LabelRuleSection, boolean>>({
    include: true,
    boost: boostLabelIds.length > 0,
    exclude: excludeLabelIds.length > 0
  })
  const [saving, setSaving] = useState(false)
  const [autoSlug, setAutoSlug] = useState(!channel.id)
  const [activeTab, setActiveTab] = useState<ChannelDialogTab>('details')

  const [coverPreview, setCoverPreview] = useState<string | null>(
    getChannelCoverUrl(channel, 'preview')
      ? resolveMediaUrl(getChannelCoverUrl(channel, 'preview')!)
      : null
  )
  const [coverFile, setCoverFile] = useState<File | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const normalizedLabelSearch = labelSearch.trim().toLowerCase()

  useEffect(() => {
    setSourceQuality(channel.source_quality ?? 'mp3_only')
  }, [channel.id, channel.source_quality])

  const activeBrowseSelectedIds = useMemo(() => {
    if (labelBrowserSection === 'include') {
      return includeLabelIds
    }

    if (labelBrowserSection === 'boost') {
      return boostLabelIds
    }

    if (labelBrowserSection === 'exclude') {
      return excludeLabelIds
    }

    return []
  }, [boostLabelIds, excludeLabelIds, includeLabelIds, labelBrowserSection])

  const browseAddedCount = browseSelectedLabelIds.filter((labelId) => !activeBrowseSelectedIds.includes(labelId)).length
  const browseRemovedCount = activeBrowseSelectedIds.filter((labelId) => !browseSelectedLabelIds.includes(labelId)).length

  const matchesLabelSearch = (label: Label): boolean =>
    !normalizedLabelSearch
    || normalizeLabelText(label.name).includes(normalizedLabelSearch)
    || normalizeLabelText(label.slug).includes(normalizedLabelSearch)
    || label.aliases.some((alias) => normalizeLabelText(alias).includes(normalizedLabelSearch))

  const parseBpmInput = (value: string): number | null => {
    const trimmed = value.trim()

    if (!trimmed) {
      return null
    }

    const parsed = Number(trimmed)

    return Number.isFinite(parsed) ? parsed : Number.NaN
  }
  const bpmMin = parseBpmInput(bpmMinInput)
  const bpmMax = parseBpmInput(bpmMaxInput)
  const bpmRangeInvalid = Number.isNaN(bpmMin)
    || Number.isNaN(bpmMax)
    || (bpmMin !== null && (bpmMin < 40 || bpmMin > 220))
    || (bpmMax !== null && (bpmMax < 40 || bpmMax > 220))
    || (bpmMin !== null && bpmMax !== null && bpmMin > bpmMax)

  const handleNameChange = (value: string): void => {
    setName(value)
    if (autoSlug) {
      setSlug(generateSlug(value))
    }
  }

  const handleSlugChange = (value: string): void => {
    setSlug(value)
    setAutoSlug(false)
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0]
    if (!file) return
    if (!file.type.startsWith('image/')) return
    setCoverFile(file)
    const reader = new FileReader()
    reader.onload = (ev) => setCoverPreview(ev.target?.result as string)
    reader.readAsDataURL(file)
  }

  const removeCover = (): void => {
    setCoverFile(null)
    setCoverPreview(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  useEffect(() => {
    if (!labelBrowserSection) {
      return undefined
    }

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [labelBrowserSection])

  const openLabelBrowser = (section: LabelRuleSection): void => {
    const selectedIds = section === 'include'
      ? includeLabelIds
      : section === 'boost'
        ? boostLabelIds
        : excludeLabelIds

    setBrowseSelectedLabelIds(selectedIds)
    setBrowseCategory('all')
    setLabelSearch('')
    setLabelBrowserSection(section)
  }

  const closeLabelBrowser = (): void => {
    setBrowseSelectedLabelIds([])
    setBrowseCategory('all')
    setLabelSearch('')
    setLabelBrowserSection(null)
  }

  const applyBrowseLabels = (): void => {
    if (labelBrowserSection === 'include') {
      setIncludeLabelIds(browseSelectedLabelIds)
    }

    if (labelBrowserSection === 'boost') {
      setBoostLabelIds(browseSelectedLabelIds)
    }

    if (labelBrowserSection === 'exclude') {
      setExcludeLabelIds(browseSelectedLabelIds)
    }

    setOpenLabelRuleSections((current) => labelBrowserSection
      ? { ...current, [labelBrowserSection]: true }
      : current)
    closeLabelBrowser()
  }

  const toggleLabelRuleSection = (section: LabelRuleSection): void => {
    setOpenLabelRuleSections((current) => ({
      ...current,
      [section]: !current[section]
    }))
  }

  const save = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault()
    if (bpmRangeInvalid) {
      return
    }

    setSaving(true)
    await props.onSave({
      id: channel.id,
      name,
      slug,
      description: description.trim() || null,
      color_theme: colorTheme,
      is_active: isActive,
      sort_order: sortOrder,
      track_selection_mode: trackSelectionMode,
      include_match_mode: includeMatchMode,
      bpm_min: bpmMin,
      bpm_max: bpmMax,
      stream_bitrate: streamBitrate,
      stream_format: streamFormat,
      source_quality: sourceQuality,
      visualGroupIds,
      audioGroupIds,
      labelRules: [
        ...includeLabelIds.map((labelId) => ({ labelId, mode: 'include' as const, weight: 1 })),
        ...excludeLabelIds.map((labelId) => ({ labelId, mode: 'exclude' as const, weight: 1 })),
        ...boostLabelIds.map((labelId) => ({ labelId, mode: 'boost' as const, weight: 1 }))
      ],
      coverFile
    })
    setSaving(false)
  }




  const renderActiveTabPanel = (): ReactNode => (
    <>
          {activeTab === 'details' && (
            <div
              className={cx('channel-tab-panel', 'channel-details-panel')}
              id="channel-tab-details"
              role="tabpanel"
              aria-labelledby="channel-tab-trigger-details"
            >
              <div className={cx('formSection')}>
                <div className={cx('formSectionTitle')}>Basic Information</div>
                <label className="form-group">
                  <span className="form-label">Name</span>
                  <input
                    className="form-input"
                    type="text"
                    placeholder="e.g. Ambient Lounge"
                    maxLength={80}
                    value={name}
                    onChange={(e) => handleNameChange(e.target.value)}
                  />
                </label>
                <label className="form-group">
                  <span className="form-label">Slug</span>
                  <input
                    className="form-input"
                    type="text"
                    placeholder="channel-slug"
                    value={slug}
                    onChange={(e) => handleSlugChange(e.target.value)}
                  />
                </label>
                <label className="form-group">
                  <span className="form-label">Description</span>
                  <textarea
                    className={cx('form-input', 'formTextarea')}
                    placeholder="Brief description of this channel..."
                    maxLength={300}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </label>
              </div>

              <div className={cx('formSection')}>
                <div className={cx('formSectionTitle')}>Publishing</div>
                <div className={cx('channel-publishing-grid')}>
                  <div className={cx('form-group', 'channel-active-row')}>
                    <div>
                      <span className="form-label">Public status</span>
                      <p className="form-label-hint">Active channels appear publicly when they have ready audio and visuals.</p>
                    </div>
                    <ToggleSwitch
                      checked={isActive}
                      label="Toggle channel active state"
                      onChange={(e) => setIsActive(e.target.checked)}
                      rootClassName={cx('channel-active-toggle')}
                    />
                  </div>
                  <label className="form-group">
                    <span className="form-label">Sort Order</span>
                    <input
                      className="form-input"
                      type="number"
                      value={sortOrder}
                      onChange={(e) => setSortOrder(parseInt(e.target.value) || 0)}
                    />
                  </label>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'appearance' && (
            <div
              className={cx('channel-tab-panel', 'channel-appearance-panel')}
              id="channel-tab-appearance"
              role="tabpanel"
              aria-labelledby="channel-tab-trigger-appearance"
            >
              <div className={cx('formSection')}>
                <div className={cx('formSectionTitle')}>Color Theme</div>
                <div className={cx('channel-theme-grid')} role="radiogroup" aria-label="Channel color theme">
                  {channelColorThemes.map((theme) => (
                    <button
                      className={cx('channel-theme-option', colorTheme === theme.id && 'selected')}
                      key={theme.id}
                      onClick={() => setColorTheme(theme.id)}
                      role="radio"
                      style={{
                        '--theme-accent': theme.accent,
                        '--theme-accent-glow': theme.accentGlow,
                        '--theme-card-tint': theme.cardTint
                      } as CSSProperties}
                      type="button"
                      aria-checked={colorTheme === theme.id}
                    >
                      <span className={cx('channel-theme-swatch')} aria-hidden="true">
                        <span />
                      </span>
                      <span>{theme.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className={cx('formSection')}>
                <div className={cx('formSectionTitle')}>Cover Image</div>
                <div
                  className={cx('cover-upload-area', coverPreview && 'has-image')}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                  />
                  {coverPreview && <img src={coverPreview} alt="Cover preview" />}
                  <button
                    className={cx('cover-upload-remove')}
                    onClick={(e) => { e.stopPropagation(); removeCover() }}
                    type="button"
                    aria-label="Remove cover"
                  >
                    <X size={12} />
                  </button>
                  <div className={cx('cover-upload-content')}>
                    <Upload size={20} className={cx('cover-upload-icon')} />
                    <div className={cx('cover-upload-text')}><strong>Click to upload</strong> or drag and drop</div>
                    <div className={cx('cover-upload-hint')}>PNG, JPG, WebP</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'visual' && (
            <div
              className={cx('channel-tab-panel', 'channel-visual-panel')}
              id="channel-tab-visual"
              role="tabpanel"
              aria-labelledby="channel-tab-trigger-visual"
            >
              <div className={cx('formSection', 'channel-content-section')}>
                <div className={cx('formSectionTitle')}>Visual Stream</div>
                <div className={cx('channel-group-picker')}>
                  <h3>Visual Groups</h3>
                  <p className="form-label-hint">Visual groups define the fullscreen stream.</p>
                  <GroupSelect
                    groups={visualGroups.map((g) => ({ id: g.id, name: g.name, count: g.asset_count }))}
                    selectedIds={visualGroupIds}
                    onChange={setVisualGroupIds}
                    placeholder="Search visual groups..."
                    emptyText="No visual groups available."
                  />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'audio' && (
            <div
              className={cx('channel-tab-panel', 'channel-audio-panel')}
              id="channel-tab-audio"
              role="tabpanel"
              aria-labelledby="channel-tab-trigger-audio"
            >
              <div className={cx('formSection', 'channel-content-section')}>
                <div className={cx('formSectionTitle')}>Audio Selection</div>
                <div className={cx('channel-selection-mode')} role="radiogroup" aria-label="Audio track selection mode">
                  <button
                    className={cx('segmented-option', trackSelectionMode === 'groups' && 'active')}
                    onClick={() => setTrackSelectionMode('groups')}
                    type="button"
                  >
                    Groups
                  </button>
                  <button
                    className={cx('segmented-option', trackSelectionMode === 'labels' && 'active')}
                    onClick={() => setTrackSelectionMode('labels')}
                    type="button"
                  >
                    Labels
                  </button>
                </div>
                <div className={cx('channel-bpm-range-panel')}>
                  <div>
                    <h3>BPM range</h3>
                    <p className="form-label-hint">Optional filter applied after groups or label rules. Tracks without BPM are skipped when a range is set.</p>
                  </div>
                  <div className={cx('channel-bpm-range-fields')}>
                    <label className="form-group">
                      <span className="form-label">Minimum BPM</span>
                      <input
                        className="form-input"
                        inputMode="decimal"
                        max={220}
                        min={40}
                        onChange={(event) => setBpmMinInput(event.target.value)}
                        placeholder="40"
                        type="number"
                        value={bpmMinInput}
                      />
                    </label>
                    <label className="form-group">
                      <span className="form-label">Maximum BPM</span>
                      <input
                        className="form-input"
                        inputMode="decimal"
                        max={220}
                        min={40}
                        onChange={(event) => setBpmMaxInput(event.target.value)}
                        placeholder="220"
                        type="number"
                        value={bpmMaxInput}
                      />
                    </label>
                  </div>
                  {bpmRangeInvalid && (
                    <p className="form-error">BPM range must use values from 40 to 220, with minimum not greater than maximum.</p>
                  )}
                </div>
                {trackSelectionMode === 'groups' ? (
                  <div className={cx('channel-group-picker', 'channel-audio-source-panel')}>
                    <h3>Audio Groups</h3>
                    <p className="form-label-hint">Audio groups define playback on public screens.</p>
                    <GroupSelect
                      groups={audioGroups.map((g) => ({ id: g.id, name: g.name, count: g.track_count }))}
                      selectedIds={audioGroupIds}
                      onChange={setAudioGroupIds}
                      placeholder="Search audio groups..."
                      emptyText="No audio groups available."
                    />
                  </div>
                ) : (
                  <div className={cx('channel-label-rules', 'channel-audio-source-panel')}>
                    <div className={cx('channel-include-match-mode')}>
                      <div>
                        <h3>Include Matching</h3>
                        <p className="form-label-hint">
                          Choose whether tracks need every include label or any one of them.
                        </p>
                      </div>
                      <div className={cx('channel-selection-mode')} role="radiogroup" aria-label="Include label matching mode">
                        <button
                          aria-checked={includeMatchMode === 'all'}
                          className={cx('segmented-option', includeMatchMode === 'all' && 'active')}
                          onClick={() => setIncludeMatchMode('all')}
                          role="radio"
                          type="button"
                        >
                          All labels
                        </button>
                        <button
                          aria-checked={includeMatchMode === 'any'}
                          className={cx('segmented-option', includeMatchMode === 'any' && 'active')}
                          onClick={() => setIncludeMatchMode('any')}
                          role="radio"
                          type="button"
                        >
                          Any label
                        </button>
                      </div>
                    </div>
                    <ChannelRuleLabelPanel
                      description={includeMatchMode === 'all'
                        ? 'Tracks must have every selected label.'
                        : 'Tracks need at least one selected label.'}
                      labels={labels}
                      onBrowse={() => openLabelBrowser('include')}
                      onChange={setIncludeLabelIds}
                      onToggle={() => toggleLabelRuleSection('include')}
                      open={openLabelRuleSections.include}
                      selectedIds={includeLabelIds}
                      title="Include Labels"
                    />
                    <ChannelRuleLabelPanel
                      description="Matching tracks get higher random priority."
                      labels={labels}
                      onBrowse={() => openLabelBrowser('boost')}
                      onChange={setBoostLabelIds}
                      onToggle={() => toggleLabelRuleSection('boost')}
                      open={openLabelRuleSections.boost}
                      selectedIds={boostLabelIds}
                      title="Boost Labels"
                    />
                    <ChannelRuleLabelPanel
                      description="Tracks with these labels are rejected."
                      labels={labels}
                      onBrowse={() => openLabelBrowser('exclude')}
                      onChange={setExcludeLabelIds}
                      onToggle={() => toggleLabelRuleSection('exclude')}
                      open={openLabelRuleSections.exclude}
                      selectedIds={excludeLabelIds}
                      title="Exclude Labels"
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'streaming' && (
            <div
              className={cx('channel-tab-panel', 'channel-streaming-panel')}
              id="channel-tab-streaming"
              role="tabpanel"
              aria-labelledby="channel-tab-trigger-streaming"
            >
              <div className={cx('formSection')}>
                <div className={cx('formSectionTitle')}>Live Stream</div>
                <div className={cx('streamSettingsGrid')}>
                  <label className="form-group">
                    <span className="form-label">Bitrate</span>
                    <select
                      className="form-input"
                      value={streamBitrate}
                      onChange={(e) => setStreamBitrate(Number(e.target.value))}
                    >
                      <option value={64}>64 kbps</option>
                      <option value={96}>96 kbps</option>
                      <option value={128}>128 kbps</option>
                      <option value={192}>192 kbps</option>
                      <option value={256}>256 kbps</option>
                      <option value={320}>320 kbps</option>
                    </select>
                  </label>
                  <div className="form-group">
                    <span className="form-label">Format</span>
                    <div className={cx('formStaticValue')}>MP3</div>
                  </div>
                </div>
                <div className="form-group">
                  <span className="form-label">Source quality</span>
                  <div className={cx('channel-selection-mode')} role="radiogroup" aria-label="Stream source quality">
                    <button
                      aria-checked={sourceQuality === 'mp3_only'}
                      className={cx('segmented-option', sourceQuality === 'mp3_only' && 'active')}
                      onClick={() => setSourceQuality('mp3_only')}
                      role="radio"
                      type="button"
                    >
                      MP3 only
                    </button>
                    <button
                      aria-checked={sourceQuality === 'prefer_lossless'}
                      className={cx('segmented-option', sourceQuality === 'prefer_lossless' && 'active')}
                      onClick={() => setSourceQuality('prefer_lossless')}
                      role="radio"
                      type="button"
                    >
                      Prefer HQ
                    </button>
                    <button
                      aria-checked={sourceQuality === 'require_lossless'}
                      className={cx('segmented-option', sourceQuality === 'require_lossless' && 'active')}
                      onClick={() => setSourceQuality('require_lossless')}
                      role="radio"
                      type="button"
                    >
                      HQ only
                    </button>
                  </div>
                </div>
              </div>

              {channel.id && (
                <div className={cx('formSection')}>
                  <div className={cx('formSectionTitle')}>Stream Control</div>
                  <StreamControlPanel channel={channel} />
                </div>
              )}
            </div>
          )}

    </>
  )

  return (
    <div className={cx('modal-backdrop', 'backdrop')} onMouseDown={(e) => { if (e.target === e.currentTarget) props.onClose() }}>
      <form className={cx('channel-modal-panel')} onSubmit={(event) => void save(event)}>
        <div className={cx('channel-modal-header')}>
          <div>
            <span className={cx('channel-mobile-kicker')}>{channel.id ? 'Edit channel' : 'Create channel'}</span>
            <h2>{channel.id ? 'Edit Channel' : 'Create Channel'}</h2>
            <p>{name.trim() || 'Untitled channel'}</p>
          </div>
          <button className={cx('btn-close-drawer')} onClick={props.onClose} type="button" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div
          className={cx('channel-modal-tabs')}
          role="tablist"
          aria-label="Channel editor sections"
          style={{ '--mobile-editor-tab-count': channelDialogTabs.length } as CSSProperties}
        >
          {channelDialogTabs.map((tab) => (
            <button
              aria-controls={`channel-tab-${tab.id}`}
              aria-selected={activeTab === tab.id}
              className={cx('channel-modal-tab', activeTab === tab.id && 'active')}
              id={`channel-tab-trigger-${tab.id}`}
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              role="tab"
              type="button"
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className={cx('channel-modal-body')}>
          {renderActiveTabPanel()}
        </div>

        <div className={cx('channel-modal-footer')}>
          <Button onClick={props.onClose}>Cancel</Button>
          <Button disabled={saving || !name.trim() || !slug.trim() || bpmRangeInvalid} type="submit" variant="primary">
            {saving ? 'Saving...' : 'Save Channel'}
          </Button>
        </div>

        <MobileEditorSheet
          activeTab={activeTab}
          actions={(
            <>
              <Button onClick={props.onClose}>Cancel</Button>
              <Button disabled={saving || !name.trim() || !slug.trim() || bpmRangeInvalid} type="submit" variant="primary">
                {saving ? 'Saving...' : 'Save Channel'}
              </Button>
            </>
          )}
          ariaLabel={channel.id ? 'Edit channel' : 'Create channel'}
          closeButton={(
            <button className={cx('btn-close-drawer')} onClick={props.onClose} type="button" aria-label="Close">
              <X size={18} />
            </button>
          )}
          kicker={channel.id ? 'Edit channel' : 'Create channel'}
          onTabChange={setActiveTab}
          subtitle={name.trim() || 'Untitled channel'}
          tabs={channelDialogTabs}
          tabsPlacement="belowHeader"
          title={channel.id ? 'Edit Channel' : 'Create Channel'}
        >
          {renderActiveTabPanel()}
        </MobileEditorSheet>
        {labelBrowserSection && (
          <LabelBrowser
            actions={(
              <>
                <Button onClick={closeLabelBrowser}>Cancel</Button>
                <Button onClick={applyBrowseLabels} variant="primary">
                  Apply {browseSelectedLabelIds.length}
                </Button>
              </>
            )}
            ariaLabel="Browse channel labels"
            body={(
              <div className={lb('label-browser-list')}>
                {labels.length === 0 && <span className="muted">Create labels first to configure label rules.</span>}
                {labelCategoryOptions.map((category) => {
                  if (browseCategory !== 'all' && browseCategory !== category) {
                    return null
                  }

                  const categoryLabels = labels.filter((label) =>
                    label.category === category
                    && label.status === 'active'
                    && matchesLabelSearch(label)
                  )

                  if (categoryLabels.length === 0) {
                    return null
                  }

                  return (
                    <div className={labelUiStyles.category} key={category}>
                      <span className={labelUiStyles.categoryTitle}>{labelCategoryLabels[category]}</span>
                      <div className={lb('label-browser-grid')}>
                        {categoryLabels.map((label) => {
                          const selected = browseSelectedLabelIds.includes(label.id)

                          return (
                            <button
                              className={lb('label-browser-item', selected && 'selected')}
                              key={label.id}
                              onClick={() => {
                                setBrowseSelectedLabelIds((current) =>
                                  current.includes(label.id)
                                    ? current.filter((id) => id !== label.id)
                                    : [...current, label.id]
                                )
                              }}
                              type="button"
                            >
                              <span className="track-suggestion-check" aria-hidden="true">
                                {selected && <Check size={13} />}
                              </span>
                              <span>
                                <strong>{label.name}</strong>
                                <em>{labelCategoryLabels[label.category]}</em>
                              </span>
                              <small>{label.track_count} tracks</small>
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
            className="channel-label-browser-panel"
            description={browseAddedCount > 0 || browseRemovedCount > 0
              ? `${browseAddedCount} added, ${browseRemovedCount} removed`
              : 'Select labels for this channel rule.'}
            onClose={closeLabelBrowser}
            title={labelBrowserSection === 'include'
              ? 'Browse include labels'
              : labelBrowserSection === 'boost'
                ? 'Browse boost labels'
                : 'Browse exclude labels'}
            tools={(
              <>
                <div className={lb('search-field', 'label-browser-search')}>
                  <Search size={16} />
                  <input
                    autoFocus
                    onChange={(event) => setLabelSearch(event.target.value)}
                    placeholder="Search labels..."
                    value={labelSearch}
                  />
                </div>
                <div className={lb('label-browser-categories')} aria-label="Label categories">
                  <button
                    className={lb('label-browser-category', browseCategory === 'all' && 'active')}
                    onClick={() => setBrowseCategory('all')}
                    type="button"
                  >
                    All
                  </button>
                  {labelCategoryOptions.map((category) => (
                    <button
                      className={lb('label-browser-category', browseCategory === category && 'active')}
                      key={category}
                      onClick={() => setBrowseCategory(category)}
                      type="button"
                    >
                      {labelCategoryLabels[category]}
                    </button>
                  ))}
                </div>
              </>
            )}
          />
        )}
      </form>
    </div>
  )
}
